import { useState, useEffect } from 'react';
import useShouldShowPrompt from './useShouldShowPrompt';

const webInstallPromptedAt = 'webInstallPromptedAt';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const useWebInstallPrompt = (): [
  BeforeInstallPromptEvent | null,
  () => void,
  () => void
] => {
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [userShouldBePromptedToInstall, handleUserSeeingInstallPrompt] = useShouldShowPrompt(webInstallPromptedAt);

  useEffect(() => {
    const beforeInstallPromptHandler = (event: Event) => {
      // Type assertion to BeforeInstallPromptEvent
      const evt = event as BeforeInstallPromptEvent;
//      evt.preventDefault();
      // check if user has already been asked
      if (userShouldBePromptedToInstall) {
        // store the event for later use
        setInstallPromptEvent(evt);
      }
    };

    window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler as EventListener);
    return () => window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler as EventListener);
  }, [userShouldBePromptedToInstall]);

  const handleInstallDeclined = () => {
    handleUserSeeingInstallPrompt();
    setInstallPromptEvent(null);
  };

  const handleInstallAccepted = () => {
    if (installPromptEvent) {
      // show native prompt
      installPromptEvent.prompt();
      // decide what to do after the user chooses
      installPromptEvent.userChoice.then(choice => {
        // if the user declined, we don't want to show the prompt again
        if (choice.outcome !== 'accepted') {
          handleUserSeeingInstallPrompt();
        }
        setInstallPromptEvent(null);
      });
    }
  };

  return [installPromptEvent, handleInstallDeclined, handleInstallAccepted];
};

export default useWebInstallPrompt;
